import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Select, MenuItem, Box, Typography, IconButton, List, ListItem, ListItemText,
  ListItemSecondaryAction, InputLabel, FormControl, Divider, Collapse,
  Autocomplete, CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';
import { formatDateISO } from '../../utils/weekUtils';

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function FolderSettings({
  open,
  onClose,
  folder,
  agents = [],
  clients = [],
  userEmail,
  onSaved,
}) {
  const isEdit = Boolean(folder);
  const [form, setForm] = useState({
    name: '',
    description: '',
    system_prompt: '',
    model: '',
    agent_id: null,
    client_id: null,
    client_name: null,
    project_id: null,
    color: '#526075',
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Project search state
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectLoading, setProjectLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // Meeting notes state
  const [notes, setNotes] = useState([]);
  const [notesOpen, setNotesOpen] = useState(true);
  const [editingNote, setEditingNote] = useState(null); // null = not editing, object = editing
  const [noteForm, setNoteForm] = useState({ title: '', content: '', meeting_date: '' });

  // Fetch projects for autocomplete
  const fetchProjects = async (query = '') => {
    setProjectLoading(true);
    try {
      const url = `${AI_ENGINE_URL}/api/v1/chat/projects${query ? `?q=${encodeURIComponent(query)}` : ''}`;
      const res = await authFetch(url);
      if (res.ok) setProjects(await res.json());
    } catch (err) {
      console.error('[FolderSettings] Failed to fetch projects:', err);
    }
    setProjectLoading(false);
  };

  // Debounced project search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProjects(projectSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [projectSearch]);

  useEffect(() => {
    if (open) {
      if (folder) {
        setForm({
          name: folder.name || '',
          description: folder.description || '',
          system_prompt: folder.system_prompt || '',
          model: folder.model || '',
          agent_id: folder.agent_id || null,
          client_id: folder.client_id || null,
          client_name: folder.client_name || null,
          project_id: folder.project_id || null,
          color: folder.color || '#526075',
        });
        fetchFiles(folder.id);
        fetchNotes(folder.id);
        // If folder has a project_id, fetch that project to show in autocomplete
        if (folder.project_id) {
          fetchProjects('').then(() => {
            // selectedProject will be set by matching in the Autocomplete value
          });
        }
      } else {
        setForm({ name: '', description: '', system_prompt: '', model: '', agent_id: null, client_id: null, client_name: null, project_id: null, color: '#526075' });
        setFiles([]);
        setNotes([]);
        setSelectedProject(null);
      }
      setEditingNote(null);
      setNoteForm({ title: '', content: '', meeting_date: '' });
      fetchProjects('');
    }
  }, [open, folder]);

  const fetchFiles = async (folderId) => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folderId}/files?user_email=${encodeURIComponent(userEmail)}`);
      if (res.ok) setFiles(await res.json());
    } catch (err) {
      console.error('[FolderSettings] Failed to fetch files:', err);
    }
  };

  const fetchNotes = async (folderId) => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folderId}/notes?user_email=${encodeURIComponent(userEmail)}`);
      if (res.ok) setNotes(await res.json());
    } catch (err) {
      console.error('[FolderSettings] Failed to fetch notes:', err);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Naam is verplicht');
      return;
    }
    try {
      const url = isEdit
        ? `${AI_ENGINE_URL}/api/v1/chat/folders/${folder.id}?user_email=${encodeURIComponent(userEmail)}`
        : `${AI_ENGINE_URL}/api/v1/chat/folders`;
      const method = isEdit ? 'PUT' : 'POST';
      const body = isEdit ? form : { ...form, user_email: userEmail };

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Opslaan mislukt');
      toast.success(isEdit ? 'Project bijgewerkt' : 'Project aangemaakt');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleFileUpload = async (e) => {
    if (!isEdit || !folder?.id) {
      toast.error('Sla het project eerst op voordat je bestanden uploadt');
      return;
    }
    const uploadFiles = Array.from(e.target.files);
    if (!uploadFiles.length) return;

    setUploading(true);
    for (const file of uploadFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folder.id}/files?user_email=${encodeURIComponent(userEmail)}`, {
          method: 'POST',
          body: formData,
        });
        if (!res.ok) throw new Error(`Upload mislukt: ${file.name}`);
      } catch (err) {
        toast.error(err.message);
      }
    }
    setUploading(false);
    fetchFiles(folder.id);
    toast.success('Bestanden geüpload');
    e.target.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    if (!(await bevestig({ titel: 'Bestand verwijderen?', tekst: 'Het bestand wordt uit dit project verwijderd. Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folder.id}/files/${fileId}?user_email=${encodeURIComponent(userEmail)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      toast.success('Bestand verwijderd');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ─── Meeting Notes handlers ──────────────────────────────
  const startNewNote = () => {
    setEditingNote('new');
    setNoteForm({ title: '', content: '', meeting_date: formatDateISO(new Date()) });
  };

  const startEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({ title: note.title, content: note.content, meeting_date: note.meeting_date || '' });
  };

  const cancelNoteEdit = () => {
    setEditingNote(null);
    setNoteForm({ title: '', content: '', meeting_date: '' });
  };

  const handleSaveNote = async () => {
    if (!noteForm.title.trim() || !noteForm.content.trim()) {
      toast.error('Titel en inhoud zijn verplicht');
      return;
    }
    try {
      if (editingNote === 'new') {
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folder.id}/notes?user_email=${encodeURIComponent(userEmail)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(noteForm),
        });
        if (!res.ok) throw new Error('Opslaan mislukt');
        toast.success('Meeting note toegevoegd');
      } else {
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folder.id}/notes/${editingNote.id}?user_email=${encodeURIComponent(userEmail)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(noteForm),
        });
        if (!res.ok) throw new Error('Opslaan mislukt');
        toast.success('Meeting note bijgewerkt');
      }
      fetchNotes(folder.id);
      cancelNoteEdit();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!(await bevestig({ titel: 'Meeting note verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folder.id}/notes/${noteId}?user_email=${encodeURIComponent(userEmail)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success('Meeting note verwijderd');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Project bewerken' : 'Nieuw Project'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField
          label="Naam"
          size="small"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          fullWidth
        />
        <TextField
          label="Beschrijving"
          size="small"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          fullWidth
        />
        <TextField
          label="Project context & instructies"
          multiline
          minRows={3}
          maxRows={8}
          value={form.system_prompt}
          onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
          fullWidth
          placeholder="Geef context over dit project die bij elk gesprek meegestuurd wordt..."
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Standaard model</InputLabel>
          <Select
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
            label="Standaard model"
          >
            <MenuItem value="">Geen voorkeur</MenuItem>
            <MenuItem value="gpt-5.5">GPT-5.5</MenuItem>
            <MenuItem value="gpt-5.4">GPT-5.4</MenuItem>
            <MenuItem value="gpt-5.4-mini">GPT-5.4 Mini</MenuItem>
            <MenuItem value="gpt-5.4-pro">GPT-5.4 Pro</MenuItem>
            <MenuItem value="gpt-5.4-nano">GPT-5.4 Nano</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Standaard agent</InputLabel>
          <Select
            value={form.agent_id || ''}
            onChange={(e) => setForm({ ...form, agent_id: e.target.value || null })}
            label="Standaard agent"
          >
            <MenuItem value="">Geen agent</MenuItem>
            {agents.map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {clients.length > 0 && (
          <FormControl size="small" fullWidth>
            <InputLabel>Klant</InputLabel>
            <Select
              value={form.client_id || ''}
              onChange={(e) => {
                const val = e.target.value || null;
                const cl = clients.find((c) => c.id === val);
                setForm({ ...form, client_id: val, client_name: cl?.name || null });
              }}
              label="Klant"
              renderValue={(val) => {
                if (!val) return 'Geen klant';
                const cl = clients.find((c) => c.id === val);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BusinessIcon sx={{ fontSize: 16 }} />
                    {cl?.name || 'Klant'}
                  </Box>
                );
              }}
            >
              <MenuItem value="">Geen klant</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Gekoppeld Project */}
        <Autocomplete
          size="small"
          options={projects}
          getOptionLabel={(option) =>
            option.project_number
              ? `${option.project_number} – ${option.project_name_full || option.project_name || ''}`
              : option.project_name_full || option.project_name || ''
          }
          value={projects.find((p) => p.id === form.project_id) || null}
          onChange={(_, newVal) => {
            setForm({ ...form, project_id: newVal ? newVal.id : null });
            setSelectedProject(newVal);
          }}
          onInputChange={(_, val, reason) => {
            if (reason === 'input') setProjectSearch(val);
          }}
          loading={projectLoading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography fontSize={13} fontWeight={600}>
                  {option.project_number} – {option.project_name_full || option.project_name}
                </Typography>
                <Typography fontSize={11} color="text.secondary">
                  {[option.klant_name, option.status, option.project_type].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Gekoppeld project"
              placeholder="Zoek een project..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <FolderSpecialIcon sx={{ fontSize: 18, color: '#adb3b5', mr: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {projectLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="Geen projecten gevonden"
          fullWidth
        />

        {/* Meeting Notes */}
        {isEdit && (
          <Box>
            <Divider sx={{ mb: 1 }} />
            <Box
              onClick={() => setNotesOpen(!notesOpen)}
              sx={{
                display: 'flex', alignItems: 'center', cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
              }}
            >
              <EventNoteIcon sx={{ fontSize: 18, color: '#526075', mr: 1 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                Meeting Notes ({notes.length})
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => { e.stopPropagation(); startNewNote(); }}
                sx={{ textTransform: 'none', fontSize: 12, mr: 0.5 }}
              >
                Toevoegen
              </Button>
              {notesOpen ? <ExpandLessIcon sx={{ fontSize: 18, color: '#adb3b5' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: '#adb3b5' }} />}
            </Box>

            <Collapse in={notesOpen}>
              {/* Note editor */}
              {editingNote && (
                <Box sx={{
                  mt: 1, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5,
                  border: '1px solid rgba(45,51,53,0.08)',
                }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Titel (bijv. Kickoff meeting)"
                      value={noteForm.title}
                      onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                      sx={{ flex: 1 }}
                      inputProps={{ style: { fontSize: 13 } }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      value={noteForm.meeting_date}
                      onChange={(e) => setNoteForm({ ...noteForm, meeting_date: e.target.value })}
                      sx={{ width: 150 }}
                      inputProps={{ style: { fontSize: 13 } }}
                    />
                  </Box>
                  <TextField
                    size="small"
                    multiline
                    minRows={3}
                    maxRows={10}
                    fullWidth
                    placeholder="Meeting notities..."
                    value={noteForm.content}
                    onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                    inputProps={{ style: { fontSize: 13 } }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                    <Button size="small" onClick={cancelNoteEdit} sx={{ textTransform: 'none', fontSize: 12 }}>
                      Annuleren
                    </Button>
                    <Button
                      size="small" variant="contained"
                      startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
                      onClick={handleSaveNote}
                      sx={{ textTransform: 'none', fontSize: 12, bgcolor: '#526075', '&:hover': { bgcolor: '#465469' } }}
                    >
                      Opslaan
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Notes list */}
              {notes.length > 0 ? (
                <List dense sx={{ mt: editingNote ? 1 : 0.5 }}>
                  {notes.map((note) => (
                    <ListItem
                      key={note.id}
                      sx={{
                        bgcolor: '#f8f9fa', borderRadius: 1, mb: 0.5,
                        border: '1px solid rgba(45,51,53,0.08)', px: 1.5,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography fontSize={13} fontWeight={600}>{note.title}</Typography>
                            {note.meeting_date && (
                              <Typography fontSize={11} color="text.secondary">
                                ({note.meeting_date})
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
                            {note.content.length > 150 ? note.content.slice(0, 150) + '...' : note.content}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton size="small" onClick={() => startEditNote(note)} sx={{ mr: 0.25 }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteNote(note.id)}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : !editingNote && (
                <Typography fontSize={12} color="text.secondary" sx={{ mt: 1 }}>
                  Geen meeting notes. Voeg notities toe die als context meegestuurd worden in chats.
                </Typography>
              )}
            </Collapse>
          </Box>
        )}

        {/* Project documenten */}
        {isEdit && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>Project documenten</Typography>
              <Button
                size="small"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={uploading}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                {uploading ? 'Uploading...' : 'Bestanden toevoegen'}
                <input type="file" hidden multiple accept=".txt,.md,.pdf,.csv,.json,.doc,.docx" onChange={handleFileUpload} />
              </Button>
            </Box>
            {files.length > 0 ? (
              <List dense sx={{ bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid rgba(45,51,53,0.08)' }}>
                {files.map((f) => (
                  <ListItem key={f.id}>
                    <ListItemText
                      primary={f.original_filename}
                      secondary={formatFileSize(f.file_size)}
                      primaryTypographyProps={{ fontSize: 13 }}
                      secondaryTypographyProps={{ fontSize: 11 }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => handleDeleteFile(f.id)}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography fontSize={12} color="text.secondary">
                Geen documenten. Upload bestanden die als context meegestuurd worden.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button variant="contained" onClick={handleSave} sx={{ bgcolor: '#526075', '&:hover': { bgcolor: '#465469' } }}>
          {isEdit ? 'Opslaan' : 'Aanmaken'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
